<template>
  <floating-menu :disabled="saving || !section">
    <template #button>
      <b-badge :variant="`${light ? 'light-' : ''}${resolveVariant(value)}`">
        {{ label }}
        <feather-icon icon="ChevronDownIcon" size="12" />
      </b-badge>
    </template>

    <template #default="{ close }">
      <a
        v-for="option in options"
        :key="option.value"
        href="#"
        class="dropdown-item"
        :class="{ active: option.value === value }"
        @click.prevent="
          close();
          update(option.value);
        "
      >
        {{ option.label }}
      </a>
    </template>
  </floating-menu>
</template>

<script>
import { BBadge } from 'bootstrap-vue';
import FloatingMenu from './FloatingMenu.vue';
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { title } from '@core/utils/filter';

/**
 * One field of a profile's section (exam voucher, labs, retake, status...),
 * editable in place from a section row. Saves through the per-section endpoint,
 * which writes only that section's field, so the rest of the section (makeup,
 * credit, classes) is never sent back and cannot be clobbered.
 */
export default {
  name: 'ProfileSectionFieldSelect',
  components: { BBadge, FloatingMenu },
  props: {
    /** The roster profile (must carry `id` and `sections`). */
    profile: { type: Object, default: null },
    sectionId: { type: String, required: true },
    field: { type: String, required: true },
    /** [{ label, value }] in display order. */
    options: { type: Array, required: true },
    resolveVariant: { type: Function, default: () => 'secondary' },
    light: { type: Boolean, default: true },
    /** What is being changed, for the failure message. */
    name: { type: String, default: 'section' },
  },
  emits: ['updated'],
  data() {
    return { saving: false };
  },
  computed: {
    section() {
      return this.profile && this.profile.sections ? this.profile.sections[this.sectionId] : undefined;
    },
    value() {
      return this.section ? this.section[this.field] : undefined;
    },
    label() {
      const option = this.options.find((entry) => entry.value === this.value);
      return option ? option.label : title(this.value || 'N/A');
    },
  },
  methods: {
    fail(message) {
      this.$toast({
        component: ToastificationContent,
        props: { title: `Failed to update ${this.name}`, icon: 'AlertTriangleIcon', variant: 'danger', text: message },
      });
    },
    update(value) {
      if (value === this.value) return;

      this.saving = true;
      store
        .dispatch('rosterStoreModule/updateProfileSections', {
          sectionId: this.sectionId,
          profileIds: [this.profile.id],
          sectionData: { [this.field]: value },
        })
        .then((response) => {
          // The endpoint reports per-profile failures in the body, not the status.
          if (response.data && response.data.failed > 0) {
            this.fail('The change was not applied.');
            return;
          }
          this.$set(this.section, this.field, value);
          this.$emit('updated', value);
        })
        .catch((error) => {
          this.fail(error.response && error.response.data ? error.response.data.message : error.message);
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>
